/**
 * This function will remove all html tags from a string.
 * @param str   The string to remove html tags from.
 * @returns     The string without html tags.
 */
export const removeHtmlTags = (str: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = str;
  // Extract the internal text from the HTML using .textContent
  const sanitizedText = tempDiv.innerText || tempDiv.textContent || '';
  return sanitizedText;
};

/**
 * This function will convert a string from snake_case or SNAKE_CASE to sentence Case.
 * @param text The string to convert.
 * @returns The converted string.
 */
export const snakeToSentenceCase = (text: string) => {
  if (!text) {
    return '';
  }

  // Replace underscores with spaces
  let sentence = text.replace(/_/g, ' ').toLowerCase();

  // Capitalize the first letter of the sentence
  sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1);

  return sentence;
};

const URL_REGEX =
  /(([a-z]+:\/\/)?(([a-z0-9-]+\.)+([a-z]{2}|aero|arpa|biz|com|coop|edu|gov|info|int|jobs|mil|museum|name|nato|net|org|pro|travel|local|internal))(:[0-9]{1,5})?(\/[a-z0-9_\-.~]+)*(\/([a-z0-9_\-.]*)(\?[a-z0-9+_\-.%=&]*)?)?(#[a-zA-Z0-9!$&'()*+.=-_~:@/?]*)?)(?=[\s,).!?;:<]|$)/gi;

const ANCHOR_TAG_REGEX = /<a\b[^>]*>[\s\S]*?<\/a>/gi;

type LinkifiedSegment = {
  hasLink: boolean;
  text: string;
};

const isInsideHtmlTag = (text: string, index: number): boolean => {
  const lastOpen = text.lastIndexOf('<', index);
  if (lastOpen === -1) {
    return false;
  }
  const nextClose = text.indexOf('>', lastOpen);
  return nextClose === -1 || nextClose >= index;
};

const linkifySegment = (segment: string): LinkifiedSegment => {
  URL_REGEX.lastIndex = 0;
  if (!URL_REGEX.test(segment)) {
    URL_REGEX.lastIndex = 0;
    return { text: segment, hasLink: false };
  }

  URL_REGEX.lastIndex = 0;
  let hasLink = false;
  let result = '';
  let lastIndex = 0;

  for (const match of segment.matchAll(URL_REGEX)) {
    const fullMatch = match[0];
    const matchIndex = match.index ?? 0;

    if (isInsideHtmlTag(segment, matchIndex)) {
      result += segment.slice(lastIndex, matchIndex + fullMatch.length);
      lastIndex = matchIndex + fullMatch.length;
      continue;
    }

    hasLink = true;
    result += segment.slice(lastIndex, matchIndex);
    const linkUrl = fullMatch.replace(/[).!?;:]+$/, '');
    const trailingPunctuation = fullMatch.slice(linkUrl.length);
    const href = /^[a-z]+:\/\//i.test(linkUrl) ? linkUrl : `//${linkUrl}`;
    result += `<a target='_blank' rel='noopener noreferrer' href='${href}'>${linkUrl}</a>${trailingPunctuation}`;
    lastIndex = matchIndex + fullMatch.length;
  }

  result += segment.slice(lastIndex);

  return { text: result, hasLink };
};

/** Linkify bare URLs while leaving URLs inside existing anchor tags unchanged. */
export const replaceLinksWithAnchorTags = (text: string): LinkifiedSegment => {
  let lastIndex = 0;
  let hasLink = false;
  const parts: string[] = [];
  let match: RegExpExecArray | null;

  ANCHOR_TAG_REGEX.lastIndex = 0;
  while ((match = ANCHOR_TAG_REGEX.exec(text)) !== null) {
    const before = linkifySegment(text.slice(lastIndex, match.index));
    if (before.hasLink) {
      hasLink = true;
    }
    parts.push(before.text, match[0]);
    lastIndex = match.index + match[0].length;
  }

  const after = linkifySegment(text.slice(lastIndex));
  if (after.hasLink) {
    hasLink = true;
  }
  parts.push(after.text);

  return { text: parts.join(''), hasLink };
};

export const extractTextFromHTML = (htmlString: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString.replace(/<[bB][rR]\s*\/?>/g, ' '), 'text/html');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function getText(node: any) {
    let text = '';
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
    } else {
      for (const childNode of node.childNodes) {
        text += getText(childNode);
      }
    }
    return text;
  }
  const resultText = getText(doc.body);
  return resultText.trim();
};
